/**
 * Atlas AI (Arc) helpers shared by the frontend and backend.
 *
 * The URL builders and parsers that used to live here moved to
 * `common/helpers/atlasUrls.ts` — they were never AI-specific.
 */

/**
 * `CreateTermLoan` → `Create Term Loan`, so an approval reads as English.
 *
 * Lives here rather than beside the approval store: that module pulls in
 * webSocketQueue and, through it, a DataSource built at import time — too heavy
 * a tail for a two-line string helper to drag into every caller.
 */
export const humanizeServiceName = (serviceName: string): string =>
  serviceName
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (c) => c.toUpperCase())
    .trim()

/**
 * The three rules below mirror SLASH_COMMAND_REGEX in
 * server/src/services/atlasai/helpers/activePrompts.ts (`/([a-z][a-z0-9_-]+)`),
 * which detects "/key" invocations in free-form chat text. Case and stored key
 * vs. typed key are reconciled server-side (lookups are case-insensitive), but
 * these three can't be: whitespace is what marks the END of a slash token in
 * running text, and the regex simply never starts a token on a non-letter, so
 * a name breaking them can never be typed as "/name" however the lookup is
 * normalized.
 */

/** Characters the token may contain (`[a-z0-9_-]`). */
const SLASH_SAFE_CHAR = /[a-z0-9_-]/i

/** The token's FIRST character must be a letter — "/2fa" matches nothing. */
const SLASH_SAFE_FIRST_CHAR = /[a-z]/i

/** `[a-z]` plus a `+` quantifier means a one-character name never matches. */
const MIN_SLASH_KEY_LENGTH = 2

/**
 * Returns the reasons a Saved Prompt name can't be invoked directly as "/name"
 * in Arc chat, each phrased to complete the sentence "typing /name won't match
 * it: …". An empty array means the name is fully slash-invocable as typed.
 *
 * Leading/trailing whitespace and letter case don't count — those are handled
 * automatically. Arc can always still find a prompt by intent
 * (trigger/description) regardless of this; it only affects the literal
 * "/name" shortcut.
 */
export function getSlashInvocationIssues(name: string): string[] {
  const trimmed = name.trim()
  // An empty name isn't "not slash-invocable", it's just not filled in yet.
  if (!trimmed) return []

  const issues: string[] = []

  // Only worth calling out separately when the character is otherwise legal
  // (a digit, "_" or "-"); anything else is already covered by the scan below.
  const firstChar = trimmed[0]
  if (SLASH_SAFE_CHAR.test(firstChar) && !SLASH_SAFE_FIRST_CHAR.test(firstChar)) {
    issues.push(`it starts with “${firstChar}” instead of a letter`)
  }

  if (trimmed.length < MIN_SLASH_KEY_LENGTH) {
    issues.push('it is only one character long')
  }

  const offenders = new Set<string>()
  for (const char of trimmed) {
    if (!SLASH_SAFE_CHAR.test(char)) offenders.add(char === ' ' ? 'a space' : `“${char}”`)
  }
  if (offenders.size > 0) issues.push(`it contains ${Array.from(offenders).join(', ')}`)

  return issues
}
